import styled from 'styled-components';

import { TakeoverDetailImage } from './TakeoverDetailImage';
import { FileDetails } from '../details/FileDetails';
import { InputImageName } from '../details/InputImageName';
import { InputImageURL } from '../details/InputImageURL';
import { useTakeoverContext } from '../../../contexts/Takeover';
import { useUploadAssetsContext } from '../../../contexts/UploadAssetsContext';
import { isValidEditableAsset, isValidPendingAsset } from '../../../utils/images';
import { Asset, TakeoverIntent, PromiseStatus } from '../../../types';
import { SpecialSection } from '../details/Styled';
import { AltTextInput } from '../../common';

const ScrollableContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundColors.backgroundSecondary};
  flex: 1 1 auto;
  overflow: auto;
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.values.base};
  row-gap: ${({ theme }) => theme.spacing.values.plusHalf};
`;

const ImageSection = styled(SpecialSection)`
  align-items: center;
  height: ${({ theme }) => theme.spacing.utils.pxToRem(328)};
  justify-content: center;
  padding: 20px;
  row-gap: ${({ theme }) => theme.spacing.values.plusHalf};
`;

export const testIds = {
  sectAltText: 'cl-index-takeover-detail-alt-txt-section',
  sectFile: 'cl-index-takeover-detail-file-section',
  sectImage: 'cl-index-takeover-detail-image-section',
  sectLabels: 'cl-index-takeover-detail-labels-section',
  sectName: 'cl-index-takeover-detail-name-section',
  sectUrl: 'cl-index-takeover-detail-url-section',
};

export const TakeoverDetail = () => {
  const { currentIntent } = useTakeoverContext();
  const { onModifyPendingAssets, pendingImages } = useUploadAssetsContext();

  const editableSelected = pendingImages.filter((pi) => {
    if (!pi.isSelected) return false;
    if (pi.processStatus === PromiseStatus.Rejected || pi.uploadStatus === PromiseStatus.Rejected) {
      return true;
    }
    return currentIntent === TakeoverIntent.EDIT
      ? isValidEditableAsset(pi)
      : isValidPendingAsset(pi);
  });

  const showSingleSelectionSections =
    editableSelected.length === 1 &&
    editableSelected[0].processStatus !== PromiseStatus.Rejected &&
    editableSelected[0].uploadStatus !== PromiseStatus.Rejected;

  return (
    <ScrollableContainer>
      <Details>
        <ImageSection data-test={testIds.sectImage}>
          <TakeoverDetailImage selectedAssets={editableSelected} />
        </ImageSection>
        {showSingleSelectionSections && (
          <>
            <section data-test={testIds.sectName}>
              <InputImageName
                onChange={(name: string) =>
                  onModifyPendingAssets([editableSelected[0].id], { name })
                }
                value={editableSelected[0].file?.name || ''}
              />
            </section>

            {editableSelected[0].asset && (
              <section data-test={testIds.sectUrl}>
                <InputImageURL value={editableSelected[0].asset?.url || ''} />
              </section>
            )}
            <section data-test={testIds.sectAltText}>
              <AltTextInput
                value={editableSelected[0].file?.altText || ''}
                onChange={(altText: string) =>
                  onModifyPendingAssets([editableSelected[0].id], { altText })
                }
                showDescription={true}
              />
            </section>
            <FileDetails
              asset={
                editableSelected[0].asset ||
                ({
                  height: editableSelected[0].file?.height,
                  mimeType: editableSelected[0].file?.mimeType,
                  size: editableSelected[0].file?.size,
                  width: editableSelected[0].file?.width,
                } as Asset)
              }
              data-test={testIds.sectFile}
            />
          </>
        )}
      </Details>
    </ScrollableContainer>
  );
};
